import * as React from 'react';

import {
    Cell,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    Header,
    PaginationState,
    useReactTable,
} from '@tanstack/react-table';
import {
    DndContext,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    closestCenter,
    type DragEndEvent,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { arrayMove, horizontalListSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DemiLightText, MediumText, RegularText } from '../../lib/Text';
import SvgIcon from '../SvgIcon';
import { Col, Row } from '../../lib/utils';
import { size } from 'lodash';
import clsx from 'clsx';
import { colors } from '../../lib/colors';

type Props = {
    data: any[];
    setData?: any;
    columns: any[];
    globalFilter?: string;
    onGlobalFilterChange?: any;
    selection?: any;
    onSelectionChange?: any;
    onRowCountChange?: any;
    columnsToSum?: string[];
    onColumnTotalsChange?: (totals: any) => void;
    width?: string | number;
    height?: string | number;
    isPagination?: boolean;
    pageSize?: number; // 페이지당 보여줄 항목 수
    autoResetPageIndex?: boolean; // 데이터/필터 변경 시 첫 페이지로 이동할지 여부
    rowHeight?: number;
    headerColor?: string; // 헤더 배경색
    // 다음, 이전
    //    () => table.previousPage()
    //!table.getCanPreviousPage()
    //    () => table.nextPage()
    //!table.getCanNextPage()
    // customPaginationComponent?: (
    //     previousPage: () => void,
    //     canPreviousPage: boolean,
    //     nextPage: () => void,
    //     canNextPage: boolean
    // ) => React.ReactNode;
    customPaginationComponent?: ({
        firstPage,
        previousPage,
        canPreviousPage,
        nextPage,
        canNextPage,
        pageIndex,
        totalPageIndex, // 전체 페이지 수
        setPageIndex,
    }: {
        firstPage: () => void;
        previousPage: () => void;
        canPreviousPage: boolean;
        nextPage: () => void;
        canNextPage: boolean;
        pageIndex?: number;
        totalPageIndex?: number; // 전체 페이지 수
        setPageIndex?: (pageIndex: number) => void; // 페이지 인덱스 설정 함수
    }) => React.ReactNode;
    isColumnVisibility?: boolean;
    //table.getAllLeafColumns()
    columnVisibility?: any;
    setColumnVisibility?: any;
    className?: string;
    onRowClick?: any;
    onRowHoverMouseEnter?: (index: number) => void;
    onRowHoverMouseLeave?: (index: number) => void;
    hoverColor?: string;

    isOrdering?: boolean; // 칼럼 드래그 앤 드롭 기능 활성화 여부

    isDimInactiveSortedColumn?: boolean; // 🔘 dim 효과 전체 on/off
    dimExcludeColumnIds?: string[]; // 🔘 예외 컬럼 id 리스트

    // 헤더와 cell 의 간격
    headerAndCellPadding?: number; // 10]

    isUnballancedRowColor?: boolean; // 불균형 테이블 여부
    // row 간의 경계선, borderBottom
    isRowBorder?: boolean; // row 간의 경계선 여부
    isHedaerDownBorder?: boolean;
    isIndex?: boolean; // ✅ index 컬럼 표시 여부
    indexColumnWidth?: number; // index 컬럼 너비
    indexFontSize?: number; // index 폰트 사이즈
    indexHeader?: string | React.ReactNode; // index 컬럼 헤더 텍스트
    emptyText?: string; // 데이터가 없을 때 표시할 텍스트
    emptyComponent?: React.ReactNode; // 데이터가 없을 때 표시할 커스텀 컴포넌트
    showHorizontalScrollHint?: boolean;
    rowRadius?: number; // row의 border-radius
    highlightOnRowHover?: {
        columnId: string;
        className: string;
    }[];
    getRowClassName?: (row: any) => string; // row의 className을 동적으로 설정하는 함수
    getRowStyle?: (row: any) => React.CSSProperties; // row의 style을 동적으로 설정하는 함수
};
function useSkipper() {
    const shouldSkipRef = React.useRef(true);
    const shouldSkip = shouldSkipRef.current;

    // Wrap a function with this to skip a pagination reset temporarily
    const skip = React.useCallback(() => {
        shouldSkipRef.current = false;
    }, []);

    React.useEffect(() => {
        shouldSkipRef.current = true;
    });

    return [shouldSkip, skip] as const;
}

const Table = React.forwardRef((props: Props, ref) => {
    const {
        data: _data,
        setData,
        columns: _columns,
        globalFilter: _globalFilter,
        onGlobalFilterChange,
        isPagination = false,
        pageSize: propPageSize = 10, // 페이지당 보여줄 항목 수
        autoResetPageIndex: shouldAutoResetPageIndex = true,
        rowHeight = 73,
        customPaginationComponent,
        selection,
        onSelectionChange,
        columnsToSum,
        onColumnTotalsChange,
        isColumnVisibility,
        columnVisibility,
        setColumnVisibility,
        width,
        className,
        height,
        onRowClick,
        onRowHoverMouseEnter,
        onRowHoverMouseLeave,
        hoverColor,
        isOrdering = false,
        isDimInactiveSortedColumn = false, // 🔘 dim 효과 전체 on/of
        dimExcludeColumnIds = [], // 🔘 예외 컬럼 id 리스트
        headerColor = '#EEF0F2', // 헤더 배경색
        headerAndCellPadding,
        isUnballancedRowColor = false, // 불균형 테이블 여부
        isRowBorder = false, // row 간의 경계선 여부
        isHedaerDownBorder = false,
        isIndex,
        indexColumnWidth = 50, // index 컬럼 너비
        indexFontSize = 13, // index 폰트 사이즈
        indexHeader = '', // index 컬럼 헤더 텍스트 기본값
        emptyText = '데이터가 없습니다.',
        emptyComponent,
        showHorizontalScrollHint = false,
        rowRadius = 15, // row의 border-radius
        highlightOnRowHover,
        getRowClassName,
        getRowStyle,
        // f
        ...otherProps
    } = props;

    const scrollContainer = React.useRef<HTMLDivElement>(null);
    const [hasHorizontalOverflow, setHasHorizontalOverflow] = React.useState(false);
    React.useEffect(() => {
        const container = scrollContainer.current;
        if (!showHorizontalScrollHint || !container) return;
        const measure = () => setHasHorizontalOverflow(container.scrollWidth > container.clientWidth + 1);
        measure();
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(measure);
        observer.observe(container);
        if (container.firstElementChild) observer.observe(container.firstElementChild);
        return () => observer.disconnect();
    }, [showHorizontalScrollHint, width]);

    const columns = [
        ...(props.isIndex
            ? [
                  {
                      id: 'index',
                      header: indexHeader,
                      cell: (info: any) => {
                          const rowIndex =
                              info.table.getRowModel().rows.findIndex((r: any) => r.id === info.row.id) + 1;

                          return (
                              <RegularText
                                  fontSize={indexFontSize}
                                  lineHeight={indexFontSize + 5}
                                  color={colors.primary}
                              >
                                  {rowIndex}
                              </RegularText>
                          );
                      },
                      size: indexColumnWidth,
                      enableSorting: false,
                      ordering: false,
                      width: indexColumnWidth,
                  },
              ]
            : []),
        ...(onSelectionChange
            ? [
                  {
                      id: 'selection',
                      header: ({ table }: { table: any }) => (
                          <IndeterminateCheckbox
                              {...{
                                  checked: table.getIsAllRowsSelected(),
                                  indeterminate: table.getIsSomeRowsSelected(),
                                  onChange: table.getToggleAllRowsSelectedHandler(),
                              }}
                          />
                      ),
                      cell: ({ row }: { row: any }) => (
                          <div className="px-1">
                              <IndeterminateCheckbox
                                  {...{
                                      checked: row.getIsSelected(),
                                      disabled: !row.getCanSelect(),
                                      indeterminate: row.getIsSomeSelected(),
                                      onChange: row.getToggleSelectedHandler(),
                                  }}
                              />
                          </div>
                      ),
                      size: 40,
                      enableSorting: false,
                      ordering: false,
                  },
              ]
            : []),
        ..._columns,
    ];
    const [columnOrder, setColumnOrder] = React.useState<string[]>(() => columns.map((c) => c.id));
    const [pagination, setPagination] = React.useState<PaginationState>({
        pageIndex: 0,
        pageSize: propPageSize,
    });
    const [skipperAutoResetPageIndex, skipAutoResetPageIndex] = useSkipper();

    // pageSize prop이 변경되면 pagination 업데이트
    React.useEffect(() => {
        setPagination((prev) => ({
            ...prev,
            pageSize: propPageSize,
            pageIndex: 0, // 페이지 크기가 변경되면 첫 페이지로 이동
        }));
    }, [propPageSize]);

    const table = useReactTable({
        data: _data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),

        state: {
            globalFilter: _globalFilter ? _globalFilter : undefined,
            rowSelection: selection ? selection : undefined,
            // columnOrder,
            pagination: isPagination ? pagination : undefined,
            columnVisibility: isColumnVisibility ? columnVisibility : undefined,
        },
        onGlobalFilterChange: onGlobalFilterChange,
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: isPagination ? getPaginationRowModel() : undefined,

        onRowSelectionChange: onSelectionChange,
        enableRowSelection: true,

        onColumnOrderChange: setColumnOrder,
        onPaginationChange: setPagination,

        onColumnVisibilityChange: setColumnVisibility,
        autoResetPageIndex: shouldAutoResetPageIndex ? skipperAutoResetPageIndex : false,
        meta: {
            updateData: (rowIndex: any, columnId: any, value: any) => {
                // Skip page index reset until after next rerender
                //   skipAutoResetPageIndex()
                skipAutoResetPageIndex();

                setData((old: any) =>
                    old.map((row: any, index: any) => {
                        if (index === rowIndex) {
                            return {
                                ...old[rowIndex]!,
                                [columnId]: value,
                            };
                        }
                        return row;
                    }),
                );
            },
        },
    });

    const filteredRowCount = table.getFilteredRowModel().rows.length;
    React.useEffect(() => {
        if (!isPagination || shouldAutoResetPageIndex) return;

        const maxPageIndex = Math.max(Math.ceil(filteredRowCount / pagination.pageSize) - 1, 0);
        setPagination((prev) => {
            if (prev.pageIndex <= maxPageIndex) return prev;
            return {
                ...prev,
                pageIndex: maxPageIndex,
            };
        });
    }, [filteredRowCount, isPagination, pagination.pageSize, shouldAutoResetPageIndex]);

    React.useImperativeHandle(ref, () => ({
        getTableInstance: () => table, // table 인스턴스 반환
        table,
    }));

    // Calculate totals for specified columns
    const calculateTotals = () => {
        const totals = columnsToSum?.reduce((acc: any, col) => {
            acc[col] = table
                .getRowModel()
                .rows.map((row) => row.getValue(col))
                .reduce((sum: any, value) => sum + (value || 0), 0);
            return acc;
        }, {});

        return totals;
    };

    // reorder columns after drag & drop
    function handleDragEnd(event: DragEndEvent) {
        const { active, over } = event;
        if (active && over && active.id !== over.id) {
            setColumnOrder((columnOrder) => {
                const oldIndex = columnOrder.indexOf(active.id as string);
                const newIndex = columnOrder.indexOf(over.id as string);
                return arrayMove(columnOrder, oldIndex, newIndex); //this is just a splice util
            });
        }
    }
    const sensors = useSensors(useSensor(MouseSensor, {}), useSensor(TouchSensor, {}), useSensor(KeyboardSensor, {}));

    // 로우 카운터

    const _hoverColor = hoverColor || '#F8F9FB';

    const Context = (isOrdering ? SortableContext : React.Fragment) as any;
    const colCount = table.getAllLeafColumns().length; // total visible columns

    return (
        <Context
            {...(isOrdering && {
                collisionDetection: closestCenter,
                modifiers: [restrictToHorizontalAxis],
                onDragEnd: handleDragEnd,
                sensors: sensors,
            })}
        >
            <div className="flex flex-col w-full relative">
                {showHorizontalScrollHint && hasHorizontalOverflow && (
                    <p className="mb-[8px] text-right text-[13px] leading-[20px] text-[#4E5968]">표를 좌우로 밀면 나머지 칸을 볼 수 있어요 ↔</p>
                )}
                <div
                    ref={scrollContainer}
                    className={clsx(
                        ' flex flex-col relative  overflow-auto scrollbar scrollbar-thumb-[#B4B4B6] scrollbar-corner-GREY_100 scrollbar-track-rounded-full scrollbar-thumb-rounded-full scrollbar-h-[10px] scrollbar-w-[6px]',
                        className,
                        showHorizontalScrollHint && 'scrollbar-track-[#EEF0F2] scrollbar-thumb-[#6B7280]',
                    )}
                    {...otherProps}
                    style={{ height: height, boxShadow: showHorizontalScrollHint && hasHorizontalOverflow ? 'inset -10px 0 12px -12px #4E5968' : undefined }}
                >
                    <table style={{ width: width }}>
                        <thead
                            className={`sticky top-0   z-[20] `}
                            style={{
                                height: headerAndCellPadding ? headerAndCellPadding + 'px' : undefined,
                                backgroundColor: headerColor,
                                borderBottom: isHedaerDownBorder ? '1px solid #E0E0E0' : undefined,
                            }}
                        >
                            {table.getHeaderGroups().map((headerGroup, index) => (
                                <tr key={headerGroup.id}>
                                    <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
                                        {headerGroup.headers.map((header) => (
                                            <DraggableTableHeader
                                                key={header.id}
                                                header={header}
                                                totalLength={headerGroup.headers.length - 1}
                                            />
                                        ))}
                                    </SortableContext>
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows?.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={colCount}
                                        className="justify-center items-center text-center pt-[100px]"
                                    >
                                        {emptyComponent ? (
                                            emptyComponent
                                        ) : (
                                            <RegularText fontSize={14} lineHeight={19} color={colors.Text_1}>
                                                {emptyText}
                                            </RegularText>
                                        )}
                                    </td>
                                </tr>
                            ) : (
                                table.getRowModel().rows.map((row, visibleIndex) => {
                                    const customClassName = getRowClassName ? getRowClassName(row.original) : '';
                                    const customStyle = getRowStyle ? getRowStyle(row.original) : {};

                                    return (
                                        <tr
                                            key={row.id} // ✅ `row.id`를 사용하여 고유 key 설정
                                            style={{ height: rowHeight, ...customStyle }}
                                            className={clsx(
                                                'group', // ✅ 추가!
                                                'border-t border-b border-gray-200 hover:bg-[#F0F2F5] items-center justify-center',
                                                onRowClick ? 'cursor-pointer' : '',
                                                isUnballancedRowColor && visibleIndex % 2 === 0 ? 'bg-[#F8F9FB]' : '',
                                                customClassName,
                                            )}
                                            onMouseEnter={() => onRowHoverMouseEnter && onRowHoverMouseEnter(row.index)}
                                            onMouseLeave={() => onRowHoverMouseLeave && onRowHoverMouseLeave(row.index)}
                                        >
                                            {row.getVisibleCells().map((cell) => (
                                                <SortableContext
                                                    items={columnOrder}
                                                    strategy={horizontalListSortingStrategy}
                                                    key={'SortableContext' + cell.id}
                                                >
                                                    <DragAlongCell
                                                        cell={cell}
                                                        onRowClick={onRowClick}
                                                        rowRadius={rowRadius} // row의 border-radius
                                                        dimExcludeColumnIds={dimExcludeColumnIds}
                                                        isDimInactiveSortedColumn={isDimInactiveSortedColumn} // 🔘 dim 효과 전체 on/off
                                                        isUnballancedRowColor={isUnballancedRowColor} // 불균형 테이블 여부
                                                        isRowBorder={isRowBorder} // row 간의 경계선 여부
                                                        highlightOnRowHover={highlightOnRowHover}
                                                    />
                                                </SortableContext>
                                                // <td key={cell.id} className=" border-solid border-[1px]">
                                                //     {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                // </td>
                                            ))}
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                        <tfoot className="">
                            {table.getFooterGroups().map((footerGroup) => (
                                <tr key={footerGroup.id}>
                                    {footerGroup.headers.map((header) => (
                                        <th key={header.id} className="">
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(header.column.columnDef.footer, header.getContext())}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </tfoot>
                    </table>
                </div>
                {isPagination ? (
                    customPaginationComponent ? (
                        customPaginationComponent({
                            firstPage: () => table.firstPage(),
                            previousPage: () => table.previousPage(),
                            canPreviousPage: !table.getCanPreviousPage(),
                            nextPage: () => table.nextPage(),
                            canNextPage: !table.getCanNextPage(),
                            pageIndex: table.getState().pagination.pageIndex,
                            totalPageIndex: table.getPageCount(),
                            setPageIndex: (pageIndex: number) => {
                                table.setPageIndex(pageIndex);
                            },
                        })
                    ) : (
                        <div className="flex items-center gap-2 my-[16px] self-center">
                            <button
                                className="border rounded p-1"
                                onClick={() => table.firstPage()}
                                disabled={!table.getCanPreviousPage()}
                            >
                                {'<<'}
                            </button>
                            <button
                                className="border rounded p-1"
                                onClick={() => table.previousPage()}
                                disabled={!table.getCanPreviousPage()}
                            >
                                {'<'}
                            </button>
                            <button
                                className="border rounded p-1"
                                onClick={() => table.nextPage()}
                                disabled={!table.getCanNextPage()}
                            >
                                {'>'}
                            </button>
                            <button
                                className="border rounded p-1"
                                onClick={() => table.lastPage()}
                                disabled={!table.getCanNextPage()}
                            >
                                {'>>'}
                            </button>
                            <span className="flex items-center gap-1">
                                <div>Page</div>
                                <strong>
                                    {table.getState().pagination.pageIndex + 1} of{' '}
                                    {table.getPageCount().toLocaleString()}
                                </strong>
                            </span>
                            <span className="flex items-center gap-1">
                                | Go to page:
                                <input
                                    type="number"
                                    min="1"
                                    max={table.getPageCount()}
                                    defaultValue={table.getState().pagination.pageIndex + 1}
                                    onChange={(e) => {
                                        const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                        table.setPageIndex(page);
                                    }}
                                    className="border p-1 rounded w-16"
                                />
                            </span>
                            {isPagination && (
                                <select
                                    value={table.getState().pagination.pageSize}
                                    onChange={(e) => {
                                        table.setPageSize(Number(e.target.value));
                                    }}
                                >
                                    {[10, 20, 30, 40, 50].map((pageSize) => (
                                        <option key={pageSize} value={pageSize}>
                                            Show {pageSize}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    )
                ) : null}
            </div>
        </Context>
    );
});

export default Table;

function IndeterminateCheckbox({
    indeterminate,
    className = '',
    ...rest
}: { indeterminate?: boolean } & React.HTMLProps<HTMLInputElement>) {
    const ref = React.useRef<HTMLInputElement>(null!);

    React.useEffect(() => {
        if (typeof indeterminate === 'boolean') {
            ref.current.indeterminate = !rest.checked && indeterminate;
        }
    }, [ref, indeterminate]);

    return <input type="checkbox" ref={ref} className={className + ' cursor-pointer'} {...rest} />;
}

const DragAlongCell = ({
    cell,
    onRowClick,
    isDimInactiveSortedColumn = false, // 🔘 dim 효과 전체 on/off
    dimExcludeColumnIds,
    isUnballancedRowColor = false, // 불균형 테이블 여부
    isRowBorder = false, // row 간의 경계선 여부
    rowRadius = 15, // row의 border-radius
    highlightOnRowHover,
}: {
    cell: Cell<unknown, unknown>;
    onRowClick: any;
    isDimInactiveSortedColumn?: boolean; // 🔘 dim 효과 전체 on/off
    dimExcludeColumnIds: string[];
    isUnballancedRowColor?: boolean; // 불균형 테이블 여부
    isRowBorder?: boolean; // row 간의 경계선 여부
    rowRadius?: number; // row의 border-radius
    highlightOnRowHover?: {
        columnId: string;
        className: string;
    }[];
}) => {
    const { isDragging, setNodeRef, transform } = useSortable({
        id: cell.column.id,
    });
    const table = cell.getContext().table;

    const sortedColumnId = table.getState().sorting?.[0]?.id;

    const isSortedColumn = sortedColumnId === cell.column.id;

    // 정렬상태
    const isSorting = table.getState().sorting.length > 0;

    const isDimmed =
        isDimInactiveSortedColumn && !isSortedColumn && !dimExcludeColumnIds.includes(cell.column.id) && isSorting;

    const style: React.CSSProperties = {
        opacity: isDragging ? 0.8 : isDimmed ? 0.3 : 1,
        position: 'relative',
        transform: CSS.Translate.toString(transform), // translate instead of transform to avoid squishing
        transition: 'width transform 0.2s ease-in-out',
        width: cell.column.getSize(),
        zIndex: isDragging ? 1 : 0,
        borderBottom: isRowBorder ? '1px solid #E5E5E5' : 'none',
        justifyContent: 'center',
        // padding: '20px 0',
        alignContent: 'center',
        height: '100%',
        // 왼쪽은 왼쪽위와 왼쪽아래만 bordeRadius
        // 오른쪽은 오른쪽위와 오른쪽아래만 borderRadius

        borderBottomLeftRadius: cell.column.getIndex() === 0 ? rowRadius + 'px' : '0',
        borderBottomRightRadius:
            cell.column.getIndex() === cell.row.getVisibleCells().length - 1 ? rowRadius + 'px' : '0',
        borderTopLeftRadius: cell.column.getIndex() === 0 ? rowRadius + 'px' : '0',
        borderTopRightRadius: cell.column.getIndex() === cell.row.getVisibleCells().length - 1 ? rowRadius + 'px' : '0',

        // 넘어갈때 ... 으로
    };
    const matchedHoverClass = highlightOnRowHover?.find((opt) => opt.columnId === cell.column.id)?.className;

    return (
        <td style={style} ref={setNodeRef} onClick={() => onRowClick && onRowClick(cell.row.original)}>
            <DemiLightText
                fontSize={16}
                lineHeight={24}
                color="#3D4244"
                className={clsx(
                    'justify-center items-center flex w-full h-full relative',
                    matchedHoverClass && `group-hover:${matchedHoverClass}`, // ✅ 동적 적용
                )}
            >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </DemiLightText>
        </td>
    );
};

const DraggableTableHeader = ({ header, totalLength }: { header: Header<unknown, unknown>; totalLength: number }) => {
    const { attributes, isDragging, listeners, setNodeRef, transform } = useSortable({
        id: header.column.id,
    });

    const style: React.CSSProperties = {
        opacity: isDragging ? 0.8 : 1,
        position: 'relative',
        transform: CSS.Translate.toString(transform), // translate instead of transform to avoid squishing
        transition: 'width transform 0.2s ease-in-out',
        whiteSpace: 'nowrap',
        width: header.column.getSize(),
        zIndex: isDragging ? 1 : 0,

        padding: '10px 0',
        borderTopLeftRadius: header.index === 0 ? '8px' : '0',
        // 맨 마지막 칼럼 border-radius
        borderTopRightRadius: header.index === totalLength ? '8px' : '0',
        borderBottomLeftRadius: header.index === 0 ? '8px' : '0',
        borderBottomRightRadius: header.index === totalLength ? '8px' : '0',
    };
    const meta = header.column.columnDef.meta as any;
    const align = meta?.align || 'center'; // 기본값을 'center'로 설정
    const isSortIconVisiblev = meta?.isSortIconVisible ?? true; // 기본값을 true로 설정

    const justify = align === 'start' ? 'justify-start' : align === 'end' ? 'justify-end' : 'justify-center';

    const isSelectionColumn = header.column.id === 'selection';
    //
    return (
        <th
            colSpan={header.colSpan}
            ref={setNodeRef}
            style={style}
            className={`cursor-pointer items-center justify-center `}
        >
            <Row className={`flex w-full items-center ${justify}`} {...attributes} {...listeners}>
                {/* {{
                    asc: ' 🔼',
                    desc: ' 🔽',
                }[header.column.getIsSorted() as string] ?? null} */}
                <Row
                    {...attributes}
                    {...listeners}
                    className="flex items-center justify-center text-center content-center "
                >
                    <DemiLightText
                        fontSize={16}
                        lineHeight={24}
                        color="#5D6166"
                        className="flex- h-full w-full justify-center items-center content-center relative "
                    >
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </DemiLightText>
                </Row>

                {isSortIconVisiblev && !isSelectionColumn && header.column.getCanSort() && (
                    <Col className="ml-[4px] z-100" onClick={header.column.getToggleSortingHandler()}>
                        {header.column.getIsSorted() === 'asc' && (
                            <>
                                <SvgIcon name="SvgSortArrow" fill={'#427BE8'} size={14} />
                            </>
                        )}
                        {header.column.getIsSorted() === 'desc' && (
                            <>
                                <SvgIcon name="SvgSortArrow" fill={'#427BE8'} size={14} className="rotate-180" />
                            </>
                        )}
                        {!header.column.getIsSorted() && (
                            <Col className="ml-[4px]">
                                <SvgIcon name={'SvgFillArrow'} size={10} fill={'#C6C8C9'} />
                                <SvgIcon name={'SvgFillArrow'} size={10} fill={'#C6C8C9'} className="rotate-180" />
                            </Col>
                        )}
                    </Col>
                )}
                {/* {!isSelectionColumn && (
                    <button {...attributes} {...listeners}>
                        🟰
                    </button>
                )} */}
            </Row>
        </th>
    );
};
